/**
 * WHAT: Background image rotator for public pages
 * WHY: Provide a consistent, rotating aesthetic across the site
 * HOW: Updates CSS variables on body::before at a fixed interval
 * IMPORTANCE: Consistent branding and visual interest
 */

import CONFIG from './config/config.js';
import {
    FOOD_GALLERY_BATCH_DELAY_MS,
    FOOD_GALLERY_LOAD_BATCH
} from './utils/backgroundImageUrl.js';

const DEFAULT_IMAGES = [];

const DEFAULT_INTERVAL_MS = 20000;
const MIN_FADE_DURATION_MS = 200;

const shouldRotateBackground = () => {
    const body = document.body;
    if (!body) return false;
    if (body.classList.contains('admin-body')) return false;

    return (
        body.classList.contains('menu') ||
        body.classList.contains('contact') ||
        body.classList.contains('merchandise') ||
        body.classList.contains('location') ||
        body.classList.contains('gallery') ||
        body.classList.contains('privacy') ||
        body.classList.contains('datadeletionpolicy')
    );
};

/** Safari/iOS WebKit stalls when dozens of images load at once — batch like the food gallery. */
const preloadImages = async (images) => {
    const results = [];
    for (let i = 0; i < images.length; i += FOOD_GALLERY_LOAD_BATCH) {
        const batch = images.slice(i, i + FOOD_GALLERY_LOAD_BATCH);
        const batchResults = await Promise.all(
            batch.map(
                (url) =>
                    new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve(true);
                        img.onerror = () => resolve(false);
                        img.src = url;
                    })
            )
        );
        results.push(...batchResults);
        if (i + FOOD_GALLERY_LOAD_BATCH < images.length) {
            await new Promise((r) => window.setTimeout(r, FOOD_GALLERY_BATCH_DELAY_MS));
        }
    }
    return results;
};

const getPageKey = (body) => {
    if (body?.dataset?.bgPage) return body.dataset.bgPage;
    const pageKeys = ['home', 'menu', 'contact', 'merchandise', 'location', 'gallery', 'privacy', 'datadeletionpolicy'];
    return pageKeys.find((key) => body.classList.contains(key)) || 'home';
};

/**
 * Resolve background image URLs for body::before.
 * Site static files (/images/*) must stay on the document origin (Vite dev server, Nginx).
 * CONFIG.getAssetUrl() prepends the API baseUrl — wrong for /images on localhost (API :3002 vs app :8080),
 * which produced a broken image flash until /uploads or other URLs applied.
 */
const normalizeImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
    }
    const path = url.startsWith('/') ? url : `/${url}`;
    if (path.startsWith('/images/') || path.startsWith('/fonts/')) {
        return path;
    }
    return CONFIG.getAssetUrl(path);
};

/** Legacy Background rows sometimes store only `photo.webp`; uploads live under /uploads/backgrounds/. */
const resolveBackgroundCatalogUrl = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('data:')) {
        return normalizeImageUrl(t);
    }
    const path = t.startsWith('/') ? t : `/${t}`;
    if (path.startsWith('/uploads/') || path.startsWith('/images/') || path.startsWith('/fonts/')) {
        return normalizeImageUrl(path);
    }
    if (/^\/[^/]+\.[a-z0-9]+$/i.test(path)) {
        return normalizeImageUrl(`/uploads/backgrounds${path}`);
    }
    return normalizeImageUrl(path);
};

/** Location uploads use /uploads/location/; API may return a bare filename. */
const resolveLocationUploadUrl = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('data:')) {
        return normalizeImageUrl(t);
    }
    const path = t.startsWith('/') ? t : `/${t}`;
    if (path.startsWith('/uploads/') || path.startsWith('/images/') || path.startsWith('/fonts/')) {
        return normalizeImageUrl(path);
    }
    const noLeading = path.replace(/^\//, '');
    return normalizeImageUrl(`/uploads/location/${noLeading}`);
};

const fetchLocationActive = async () => {
    try {
        const response = await fetch(CONFIG.getApiUrl('/location/active'), {
            credentials: 'include',
            headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data?.success || !data?.data) return null;
        return data.data;
    } catch {
        return null;
    }
};

/**
 * Match Find Us / location.js intent: images flagged for the location page, else brick-and-mortar
 * (permanent) location photos, else any active location, else merged backgroundImages from the API.
 */
const collectLocationPageBackgroundUrls = (activeData) => {
    if (!activeData) return [];
    const allBg = activeData.backgroundImages || [];
    const explicit = allBg.filter((img) => img?.useForLocationPage === true && img?.isActive !== false);
    const toUrls = (imgs) =>
        imgs
            .map((img) => resolveLocationUploadUrl(img.filename || img.imageUrl || img.url))
            .filter(Boolean);

    if (explicit.length > 0) {
        return toUrls(explicit);
    }

    const locs = Array.isArray(activeData.locations) ? activeData.locations : [];
    const isActiveLoc = (l) => l?.status === 'active' || l?.isActive === true;
    const activeLocs = locs.filter(isActiveLoc);

    const gatherImages = (locationList) => {
        const out = [];
        for (const loc of locationList) {
            const locImages = [...(loc.images || []), ...(loc.backgroundImages || [])];
            for (const img of locImages) {
                if (img && img.isActive !== false) out.push(img);
            }
        }
        return out;
    };

    const permanent = activeLocs.filter((l) => l?.type === 'permanent');
    let imgs = gatherImages(permanent);
    if (imgs.length === 0) {
        imgs = gatherImages(activeLocs);
    }
    if (imgs.length > 0) {
        return toUrls(imgs);
    }

    const rest = allBg.filter((img) => img?.isActive !== false);
    return toUrls(rest);
};

const fetchBackgroundConfig = async (pageKey) => {
    try {
        const response = await fetch(CONFIG.getApiUrl(`/backgrounds/page/${pageKey}`), {
            credentials: 'include'
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data?.success || !data?.data) return null;
        return data.data;
    } catch (error) {
        return null;
    }
};

const getBaseOpacity = (body) => {
    const computedOpacity = parseFloat(getComputedStyle(body, '::before').opacity);
    if (Number.isFinite(computedOpacity)) return computedOpacity;
    return 0.3;
};

/**
 * @param {boolean} [immediate] - If true, apply image/opacity now (no opacity-0 delay). Use for first paint to avoid a white/blank flash before the background appears.
 */
const setBackgroundImage = (body, url, baseOpacity, fadeDurationMs, immediate = false) => {
    if (url === null || url === undefined) {
        body.style.setProperty('--page-bg-fade', '0ms');
        body.style.setProperty('--page-bg-image', 'none');
        body.style.setProperty('--page-bg-opacity', String(baseOpacity));
        return;
    }
    if (immediate) {
        body.style.setProperty('--page-bg-fade', '0ms');
        body.style.setProperty('--page-bg-image', `url('${url}')`);
        body.style.setProperty('--page-bg-opacity', String(baseOpacity));
        return;
    }
    body.style.setProperty('--page-bg-fade', `${fadeDurationMs}ms`);
    body.style.setProperty('--page-bg-opacity', '0');
    window.setTimeout(() => {
        body.style.setProperty('--page-bg-image', `url('${url}')`);
        body.style.setProperty('--page-bg-opacity', String(baseOpacity));
    }, fadeDurationMs);
};

const shuffleArray = (items) => {
    const list = [...items];
    for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
};

const pickNextImage = (images, currentIndex, mode = 'random') => {
    if (!images.length) return 0;
    if (images.length === 1) return 0;

    if (mode === 'sequential' || mode === 'shuffle_once') {
        return (currentIndex + 1) % images.length;
    }

    let nextIndex = currentIndex;
    while (nextIndex === currentIndex) {
        nextIndex = Math.floor(Math.random() * images.length);
    }
    return nextIndex;
};

const initScrollRotation = (body, rotationImages, baseOpacity, fadeDurationMs, rotationSettings) => {
    const selectionMode = rotationSettings.selectionMode || 'random';
    const scrollStep = Math.max(0.5, Number(rotationSettings.scrollStep || 1));
    let currentIndex = 0;
    let currentStep = 0;
    const history = [currentIndex];
    let ticking = false;

    setBackgroundImage(body, rotationImages[currentIndex], baseOpacity, fadeDurationMs, true);

    const handleScroll = () => {
        if (ticking) return;
        ticking = true;

        window.requestAnimationFrame(() => {
            const stepHeight = window.innerHeight * scrollStep;
            const newStep = Math.floor(window.scrollY / Math.max(stepHeight, 1));

            if (newStep !== currentStep) {
                if (newStep > currentStep) {
                    const nextIndex = pickNextImage(rotationImages, currentIndex, selectionMode);
                    currentIndex = nextIndex;
                    history[newStep] = currentIndex;
                    setBackgroundImage(body, rotationImages[currentIndex], baseOpacity, fadeDurationMs, false);
                } else {
                    const previousIndex = history[newStep];
                    if (Number.isFinite(previousIndex)) {
                        currentIndex = previousIndex;
                        setBackgroundImage(body, rotationImages[currentIndex], baseOpacity, fadeDurationMs, false);
                    }
                }
                currentStep = newStep;
            }

            ticking = false;
        });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
};

const getImagesFromDataset = (body) => {
    const raw = body.dataset.bgImages;
    if (!raw) return null;
    const parsed = raw
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    return parsed.length > 0 ? parsed : null;
};

/** Stock cookie used as a non-legal interior fallback — never behind Privacy / Data deletion (readability + Meta URLs). */
const isStockCookies2Background = (url) => {
    if (!url || typeof url !== 'string') return false;
    const path = url.split('?')[0].toLowerCase();
    return /\/images\/backgrounds\/cookies2\.jpe?g$/.test(path);
};

const initBackgroundRotator = async () => {
    if (!shouldRotateBackground()) return;

    const body = document.body;
    const pageKey = getPageKey(body);

    // Landing page (home) uses a black body background; rotating images are used in content
    // sections (hero, food gallery, etc.) via index-init and slideshow, not as body background.
    if (pageKey === 'home') {
        return;
    }

    const baseOpacity = getBaseOpacity(body);
    body.style.setProperty('--page-bg-opacity', String(baseOpacity));

    const [backgroundConfig, locationActive] = await Promise.all([
        fetchBackgroundConfig(pageKey),
        pageKey === 'location' ? fetchLocationActive() : Promise.resolve(null)
    ]);

    let apiImages = backgroundConfig?.images
        ? backgroundConfig.images
            .filter((img) => img?.isActive !== false)
            .map((img) => resolveBackgroundCatalogUrl(img.filename || img.imageUrl || img.url))
            .filter(Boolean)
        : [];

    if (pageKey === 'location' && locationActive) {
        const fromLocationApi = collectLocationPageBackgroundUrls(locationActive);
        if (fromLocationApi.length > 0) {
            apiImages = fromLocationApi;
        }
    }

    const customImages = getImagesFromDataset(body);
    const images = customImages || apiImages || DEFAULT_IMAGES;

    const validFlags = await preloadImages(images);
    let rotationImages = images.filter((_, idx) => validFlags[idx]);
    if (pageKey === 'privacy' || pageKey === 'datadeletionpolicy') {
        rotationImages = rotationImages.filter((u) => !isStockCookies2Background(u));
    }

    const rotationSettings = backgroundConfig?.rotationSettings || {};
    const intervalAttr = parseInt(body.dataset.bgInterval, 10);
    const intervalMs = Number.isFinite(intervalAttr)
        ? intervalAttr * 1000
        : (rotationSettings.interval ? rotationSettings.interval * 1000 : DEFAULT_INTERVAL_MS);

    const fadeDurationMs = Math.max(
        MIN_FADE_DURATION_MS,
        Number(rotationSettings.transitionDuration || MIN_FADE_DURATION_MS)
    );

    const rotateNow =
        typeof window !== 'undefined' && window.location.search.includes('rotateNow=1');
    const bumpStartIndex = rotateNow && rotationImages.length > 1 ? 1 % rotationImages.length : 0;

    // If rotation is explicitly disabled for this page, do not apply any
    // background images at all – fall back to the CSS page background
    // (typically solid/black), as configured in the admin UI.
    if (rotationSettings.enabled === false) {
        return;
    }

    // API returned no usable images: legal pages and location should stay on the solid page canvas
    // (styles.css body background). Other interior pages keep a branded cookie fallback so empty
    // admin pools still look intentional instead of a flat void.
    if (rotationImages.length === 0) {
        if (pageKey === 'location' || pageKey === 'privacy' || pageKey === 'datadeletionpolicy') {
            setBackgroundImage(body, null, baseOpacity, fadeDurationMs, true);
            return;
        }
        const fallback = normalizeImageUrl('/images/backgrounds/cookies2.jpg');
        if (fallback) {
            setBackgroundImage(body, fallback, baseOpacity, fadeDurationMs, true);
        }
        return;
    }

    if (rotationImages.length < 2) {
        setBackgroundImage(body, rotationImages[bumpStartIndex], baseOpacity, fadeDurationMs, true);
        return;
    }

    if (rotationSettings.changeOnScroll) {
        initScrollRotation(body, rotationImages, baseOpacity, fadeDurationMs, rotationSettings);
        return;
    }

    const selectionMode = rotationSettings.selectionMode || 'sequential';
    let playlist = rotationImages;
    if (selectionMode === 'shuffle_once') {
        playlist = shuffleArray(rotationImages);
    }

    let currentIndex = bumpStartIndex;
    setBackgroundImage(body, playlist[currentIndex], baseOpacity, fadeDurationMs, true);

    window.setInterval(() => {
        currentIndex = pickNextImage(playlist, currentIndex, selectionMode);
        setBackgroundImage(body, playlist[currentIndex], baseOpacity, fadeDurationMs, false);
    }, intervalMs);
};

export { initBackgroundRotator };
